// ----------------------------------------------------------------------------
// Application JavaScript
// ----------------------------------------------------------------------------
//
// All scripts are loaded to the project through this master script sheet,
// which bundles our code via Webpack.
//
// Includes are listed alphabetically for easiest identification.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import './libs/feature-detect'; // Feature Detection
import './libs/a11y';           // Accessibility
import './libs/spritesheet';    // Spritesheet
import './libs/just-validate';  // Just-Validate
// import './libs/webfonts';       // Web Fonts (optional)


// -------------------------------------
// Load Base Styles
// -------------------------------------

// Global Styles
import 'normalize.css/normalize.css';
import 'styles/base/helpers.scss';
import 'styles/base/html-tags.scss';
import 'styles/base/layout.scss';
import 'styles/base/placeholders.scss';

// Button Styles
import 'styles/base/button/button.scss';
import 'styles/base/button/button--text.scss';
import 'styles/base/button/button--transparent.scss';

// Animations
import 'styles/base/animations/animations';

// Specialty Styles
// TODO: these should probably move?
import 'styles/base/product.scss';
import 'styles/base/carousel.scss';
import 'styles/base/_forms.scss';


// -------------------------------------
// Load Snippets
// -------------------------------------
import 'snippets/toggletip/toggletip';

// -------------------------------------
// Load Sections
// -------------------------------------

import {load} from '@shopify/theme-sections';

const initRebuySplideNormalization = () => {
    if (window.__rebuySplideNormalizationInitialized || !document.body.classList.contains('template-product')) {
        return;
    }

    window.__rebuySplideNormalizationInitialized = true;

    const rebuyHiddenBlockSelector = [
        '.rebuy-cart__flyout-announcement-bar .splide .rebuy-product-block:not(.splide__slide)',
        '.rebuy-reorder__announcement-bar .splide .rebuy-product-block:not(.splide__slide)',
        '.rebuy-widget .splide .rebuy-product-block:not(.splide__slide)',
        '.rebuy-recommended-products .splide .rebuy-product-block:not(.splide__slide)',
        '.rebuy-landing-page .splide .rebuy-product-block:not(.splide__slide)',
        '[data-rebuy-component="announcement-bar"] .splide .rebuy-product-block:not(.splide__slide)'
    ].join(', ');

    const normalizeBlocks = () => {
        document.querySelectorAll(rebuyHiddenBlockSelector).forEach((block) => {
            block.classList.add('splide__slide');
            block.style.setProperty('display', 'flex', 'important');
        });
    };

    let queued = false;
    const queueNormalize = () => {
        if (queued) {
            return;
        }

        queued = true;
        window.requestAnimationFrame(() => {
            queued = false;
            normalizeBlocks();
        });
    };

    queueNormalize();

    const observer = new MutationObserver(queueNormalize);
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden']
    });

    document.addEventListener('cart:updated', queueNormalize);
};

const initCartMutationObservers = () => {
    if (window.__cartMutationObserversInitialized) {
        return;
    }

    window.__cartMutationObserversInitialized = true;

    const cartMutationPaths = ['/cart/add.js', '/cart/change.js', '/cart/update.js', '/cart/clear.js'];
    let cartUpdatedTimeout = null;

    const isCartMutationRequest = (url) => {
        if (!url) {
            return false;
        }

        return cartMutationPaths.some(path => url.includes(path));
    };

    const emitCartUpdated = () => {
        if (cartUpdatedTimeout) {
            clearTimeout(cartUpdatedTimeout);
        }

        cartUpdatedTimeout = setTimeout(() => {
            document.dispatchEvent(new CustomEvent('cart:updated'));
        }, 120);
    };

    if (typeof window.fetch === 'function') {
        const originalFetch = window.fetch.bind(window);

        window.fetch = (...args) => {
            const requestInput = args[0];
            const requestInit = args[1] || {};

            const requestUrl = typeof requestInput === 'string' ? requestInput : requestInput?.url;
            const requestMethod = (
                requestInit.method ||
                (typeof requestInput !== 'string' ? requestInput?.method : null) ||
                'GET'
            ).toUpperCase();

            return originalFetch(...args).then((response) => {
                if (response?.ok && requestMethod !== 'GET' && isCartMutationRequest(requestUrl)) {
                    emitCartUpdated();
                }

                return response;
            });
        };
    }

    const originalXHROpen = window.XMLHttpRequest?.prototype?.open;
    const originalXHRSend = window.XMLHttpRequest?.prototype?.send;

    if (originalXHROpen && originalXHRSend) {
        window.XMLHttpRequest.prototype.open = function(method, url, ...rest) {
            this.__cartMutationMethod = method ? method.toUpperCase() : 'GET';
            this.__cartMutationUrl = typeof url === 'string' ? url : '';
            this.__isCartMutationRequest = this.__cartMutationMethod !== 'GET' && isCartMutationRequest(this.__cartMutationUrl);

            return originalXHROpen.call(this, method, url, ...rest);
        };

        window.XMLHttpRequest.prototype.send = function(...sendArgs) {
            if (this.__isCartMutationRequest) {
                this.addEventListener('load', () => {
                    if (this.status >= 200 && this.status < 300) {
                        emitCartUpdated();
                    }
                });
            }

            return originalXHRSend.apply(this, sendArgs);
        };
    }
};

initCartMutationObservers();
initRebuySplideNormalization();

document.addEventListener('DOMContentLoaded', () => {
    load('*');
});


// -------------------------------------
// Load Templates
// -------------------------------------

import 'templates/404/404';
import 'templates/article/article';
import 'templates/blog/blog';
import 'templates/cart/cart';
import 'templates/collections/collection';
import 'templates/gift-card/gift_card';
import 'templates/index/index';
import 'templates/pages/pages';
import 'templates/products/product';
import 'templates/search/search';
import 'templates/customers/account/account';
import 'templates/customers/addresses/addresses';
import 'templates/customers/login/login';
import 'templates/customers/order/order';
import 'templates/password/password';
// TODO: how should we include activate_account, register and reset_password template
